import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useDiffSetUrls } from 'src/components/diff-set/urls';
import { getGlobalShouldIgnoreWhitespace } from 'src/redux/diff-settings';
import {
  pullRequestRenderingLimits,
  getCurrentPullRequestInPreviewDiffMode,
  getFileDiffStatus,
  getIsSingleFileModeActive,
  getPullRequestDestinationHash,
  getPullRequestDestinationRepo,
  getPullRequestSourceHash,
  getPullRequestSourceRepo,
} from 'src/redux/pull-request/selectors';
import {
  getViewEntireFileParsedDiffFile,
  getIsViewEntireFileTooLarge,
  getViewEntireFileHasError,
  getIsViewEntireFileLoading,
  getViewEntireFilePath,
  closeViewEntireFileDialog,
  finishedClosingViewEntireFileDialog,
  viewEntireFile,
} from 'src/redux/pull-request/view-entire-file-reducer';
import {
  getBranchViewDestinationBranchHash,
  getBranchViewDestinationRepoName,
  getBranchViewSourceBranchHash,
} from 'src/redux/repository/branch/branch-redux';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import { Diff } from 'src/types/pull-request';
import { hasRenderableLines } from 'src/utils/diff-classifications';

type hashAndRepoTypes = {
  sourceHash: string;
  destinationHash: string;
  sourceRepoFullSlug: string;
  destinationRepoFullSlug: string;
};

// return the PR specific hashes and repo names
export function usePullRequestHashesAndRepos(): hashAndRepoTypes {
  const PRDestRepo = useSelector(getPullRequestDestinationRepo);
  const destinationRepoFullSlug = PRDestRepo ? PRDestRepo.full_name : '';
  const PRSourceRepo = useSelector(getPullRequestSourceRepo);
  const sourceRepoFullSlug = PRSourceRepo ? PRSourceRepo.full_name : '';
  const destinationHash = useSelector(getPullRequestDestinationHash) || '';
  const sourceHash = useSelector(getPullRequestSourceHash) || '';

  return {
    destinationRepoFullSlug,
    sourceRepoFullSlug,
    destinationHash,
    sourceHash,
  };
}

// return the branch specific hashes and repo names
function useBranchHashesAndRepos(): hashAndRepoTypes {
  const destinationRepoFullSlug =
    useSelector(getBranchViewDestinationRepoName) || '';
  const destinationHash = useSelector(getBranchViewDestinationBranchHash) || '';
  const sourceRepoFullSlug = useSelector(getCurrentRepositoryFullSlug) || '';
  const sourceHash = useSelector(getBranchViewSourceBranchHash) || '';

  return {
    destinationRepoFullSlug,
    sourceRepoFullSlug,
    destinationHash,
    sourceHash,
  };
}

export const useViewEntireFileState = (
  hashAndRepoSelector: () => hashAndRepoTypes,
  allowComments: boolean
) => {
  const dispatch = useDispatch();

  const path = useSelector(getViewEntireFilePath);
  const diffFile = useSelector(getViewEntireFileParsedDiffFile);
  const fileDiffStatus = useSelector(state => getFileDiffStatus(state, path));
  const prevFilePath =
    fileDiffStatus === 'renamed' ? diffFile?.from : undefined;
  const isTooLarge = useSelector(getIsViewEntireFileTooLarge);
  const hasError = useSelector(getViewEntireFileHasError);
  const isLoading = useSelector(getIsViewEntireFileLoading);
  const {
    sourceHash,
    destinationHash,
    sourceRepoFullSlug,
    destinationRepoFullSlug,
  } = hashAndRepoSelector();

  const handleClose = useCallback(
    () => dispatch(closeViewEntireFileDialog()),
    [dispatch]
  );

  const handleCloseComplete = useCallback(
    () => dispatch(finishedClosingViewEntireFileDialog()),
    [dispatch]
  );

  return {
    diffFile,
    isTooLarge,
    hasError,
    isLoading,
    path,
    prevFilePath,
    fileDiffStatus,
    destinationRepoFullSlug,
    sourceRepoFullSlug,
    destinationHash,
    sourceHash,
    allowComments,
    handleClose,
    handleCloseComplete,
  };
};

export const useBranchViewEntireFileState = () => {
  return useViewEntireFileState(useBranchHashesAndRepos, false);
};

export const usePRViewEntireFileState = () => {
  return useViewEntireFileState(usePullRequestHashesAndRepos, true);
};

export const useIsViewEntireFileSupported = (diff: Diff) => {
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);

  return hasRenderableLines(
    diff,
    isSingleFileModeActive,
    pullRequestRenderingLimits
  );
};

export const useViewEntireFile = (
  filepath: string,
  sourceHash: string,
  sourceRepoFullSlug: string,
  destinationHash?: string,
  destRepoName?: string
): (() => void) => {
  const dispatch = useDispatch();
  const ignoreWhitespace = useSelector(getGlobalShouldIgnoreWhitespace);

  const topicDiff = !useSelector(getCurrentPullRequestInPreviewDiffMode);

  // We have to check if this is the case when we are directed here from the PR page.
  // The PR page could look at an individual commit that is the latest, making the source
  // and destination hash the same. In this case we want to use the destination hash for
  // viewing the entire file for that commit.
  const sourceHashIsNotDestHash = sourceHash !== destinationHash;

  const { diffUrlBuilder } = useDiffSetUrls(
    sourceRepoFullSlug,
    sourceHashIsNotDestHash ? sourceHash : destinationHash,
    sourceHashIsNotDestHash ? destRepoName : undefined,
    sourceHashIsNotDestHash ? destinationHash : undefined
  );

  const diffUrl = diffUrlBuilder(
    ignoreWhitespace,
    true // in this instance don't use server side diff length check
  );
  const onViewEntireFile = useCallback(() => {
    dispatch(viewEntireFile(filepath, diffUrl, topicDiff));
  }, [filepath, diffUrl, dispatch, topicDiff]);

  return onViewEntireFile;
};
